/**
 * Open a chat conversation from anywhere (PLAN §3.5): by channel id, or by
 * user id — the existing DM, or a new one (`POST /chat/dms`). One place for
 * what the palette, the chat sidebar and the two share modals each did by
 * hand (createDm → upsertChannel → setActiveChannel → clearUnread).
 */
import chatService from '../services/chatService';
import { useChatStore } from '../store/chatStore';
import { setActiveTab } from '../store/uiModalStore';
import { dmOther } from '../components/chatHelpers';

/** The DM with `userId`, created on the server when there is none yet; kept in the store. */
export async function ensureDm(userId) {
  const channel = await chatService.createDm(userId);
  useChatStore.getState().upsertChannel(channel);
  return channel;
}

/** Make `channelId` the active conversation and mark it read locally. */
export function activateChannel(channelId) {
  const chat = useChatStore.getState();
  chat.setActiveChannel(channelId);
  chat.clearUnread(channelId);
}

/** The existing DM channel with `userId` in the store, or null. */
export function findDmWith(userId) {
  const { channels, currentUserId } = useChatStore.getState();
  return (channels || []).find((c) => c.kind === 'dm' && String(dmOther(c, currentUserId)?.user_id) === String(userId)) || null;
}

/**
 * Chat page + that conversation. `{ channelId }` opens it; `{ userId }` opens
 * the DM with that person, creating it when needed. Resolves the channel id.
 */
export async function openChatTarget({ channelId = null, userId = null } = {}) {
  setActiveTab('chat.panel');
  if (channelId != null) {
    activateChannel(channelId);
    return channelId;
  }
  if (userId == null) return null;
  const existing = findDmWith(userId);
  if (existing) {
    activateChannel(existing.id);
    return existing.id;
  }
  const channel = await ensureDm(userId);
  activateChannel(channel.id);
  return channel.id;
}
